import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { MatTabsModule } from '@angular/material/tabs';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { MatCheckboxModule } from '@angular/material/checkbox';

import { HttpClient } from '@angular/common/http';
import { ApiService } from '../../core/services/api.service';
import { environment } from '../../../enviroment';
import { ApiResponse } from '../../core/models/api.models';
import { map } from 'rxjs';

interface IncomeRow {
  id: string;
  title: string;
  description?: string;
  amount: number;
  currency: string;
  incomeDate: string;
  category: string;
  source?: string;
  referenceNumber?: string;
  status: number;
  notes?: string;
  isSettled?: boolean;
  settledExpenseId?: string | null;
}

interface ExpenseRow {
  id: string;
  title: string;
  description?: string;
  amount: number;
  currency: string;
  expenseDate: string;
  category: string;
  status: number;
  receiptUrl?: string | null;
  invoiceScreenshotFileName?: string | null;
  isSettled?: boolean;
  settledIncomeId?: string | null;
}

interface FinanceSummary {
  incomeTotal: number;
  expenseTotal: number;
  net: number;
  incomeCount: number;
  expenseCount: number;
}

interface FinanceCategory {
  id: string;
  name: string;
  sortOrder: number;
  isDefault: boolean;
}

@Component({
  selector: 'app-finance',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatTabsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTableModule,
    MatCheckboxModule
  ],
  templateUrl: './finance.component.html',
  styleUrl: './finance.component.scss'
})
export class FinanceComponent implements OnInit {
  private readonly api = inject(ApiService);
  private readonly http = inject(HttpClient);

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly info = signal<string | null>(null);
  readonly summary = signal<FinanceSummary | null>(null);
  readonly incomes = signal<IncomeRow[]>([]);
  readonly expenses = signal<ExpenseRow[]>([]);
  readonly categories = signal<FinanceCategory[]>([]);

  incomeColumns = ['title', 'category', 'source', 'date', 'amount', 'settled', 'actions'];
  expenseColumns = ['title', 'category', 'date', 'amount', 'settled', 'screenshot', 'actions'];
  categoryColumns = ['name', 'type', 'actions'];

  // Income form
  incomeTitle = '';
  incomeAmount: number | null = null;
  incomeDate = new Date().toISOString().slice(0, 10);
  incomeCategory = '';
  incomeSource = '';
  incomeReference = '';
  incomeNotes = '';
  incomeCsvCategory = '';

  // Expense form
  expenseTitle = '';
  expenseAmount: number | null = null;
  expenseDate = new Date().toISOString().slice(0, 10);
  expenseCategory = '';
  expenseDescription = '';
  expenseFile: File | null = null;
  expensePreviewUrl: string | null = null;
  expenseCsvCategory = '';
  settleAsIncome = false;

  // Category form
  newCategoryName = '';

  ngOnInit(): void {
    this.reload();
  }

  reload(): void {
    this.loading.set(true);
    this.error.set(null);
    this.loadCategories();

    this.api.get<FinanceSummary>('v1/finance').subscribe({
      next: (s) => this.summary.set(s),
      error: () => this.summary.set(null)
    });

    this.api.getPaged<IncomeRow>('v1/incomes', { page: 1, pageSize: 100 }).subscribe({
      next: (page) => this.incomes.set(page.items ?? []),
      error: (err) => this.error.set(err?.message || 'Failed to load incomes.')
    });

    this.api.getPaged<ExpenseRow>('v1/expenses', { page: 1, pageSize: 100 }).subscribe({
      next: (page) => {
        this.expenses.set(page.items ?? []);
        this.loading.set(false);
      },
      error: (err) => {
        this.loading.set(false);
        this.error.set(err?.message || 'Failed to load expenses.');
      }
    });
  }

  loadCategories(): void {
    this.api.get<FinanceCategory[]>('v1/finance-categories').subscribe({
      next: (items) => {
        const list = items ?? [];
        this.categories.set(list);
        if (!this.incomeCategory && list.length) this.incomeCategory = list[0].name;
        if (!this.expenseCategory && list.length) this.expenseCategory = list[0].name;
        // Keep selection if still present; otherwise fall back to first
        if (this.incomeCategory && !list.some((c) => c.name === this.incomeCategory) && list.length) {
          this.incomeCategory = list[0].name;
        }
        if (this.expenseCategory && !list.some((c) => c.name === this.expenseCategory) && list.length) {
          this.expenseCategory = list[0].name;
        }
      },
      error: (err) => this.error.set(err?.message || 'Failed to load categories.')
    });
  }

  createCategory(): void {
    const name = this.newCategoryName.trim();
    if (!name) {
      this.error.set('Category name is required.');
      return;
    }

    this.api.post<FinanceCategory>('v1/finance-categories', { name }).subscribe({
      next: (created) => {
        this.newCategoryName = '';
        this.loadCategories();
        if (created?.name) {
          this.incomeCategory = created.name;
          this.expenseCategory = created.name;
        }
      },
      error: (err) => this.error.set(err?.error?.message || err?.message || 'Failed to add category.')
    });
  }

  deleteCategory(row: FinanceCategory): void {
    this.api.delete(`v1/finance-categories/${row.id}`).subscribe({
      next: () => this.loadCategories(),
      error: (err) => this.error.set(err?.error?.message || err?.message || 'Failed to delete category.')
    });
  }

  createIncome(): void {
    if (!this.incomeTitle.trim() || !this.incomeAmount || this.incomeAmount <= 0) {
      this.error.set('Income title and a positive amount are required.');
      return;
    }
    if (!this.incomeCategory) {
      this.error.set('Select an income category.');
      return;
    }

    this.api
      .post('v1/incomes', {
        title: this.incomeTitle.trim(),
        amount: this.incomeAmount,
        incomeDate: this.incomeDate,
        category: this.incomeCategory,
        source: this.incomeSource || null,
        referenceNumber: this.incomeReference || null,
        notes: this.incomeNotes || null,
        currency: 'USD',
        status: 1
      })
      .subscribe({
        next: () => {
          this.info.set('Income saved.');
          this.incomeTitle = '';
          this.incomeAmount = null;
          this.incomeSource = '';
          this.incomeReference = '';
          this.incomeNotes = '';
          this.reload();
        },
        error: (err) => this.error.set(err?.error?.message || err?.message || 'Failed to save income.')
      });
  }

  deleteIncome(row: IncomeRow): void {
    this.api.delete(`v1/incomes/${row.id}`).subscribe({
      next: () => this.reload(),
      error: (err) => this.error.set(err?.message || 'Failed to delete income.')
    });
  }

  onExpenseFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    this.expenseFile = file;
    if (this.expensePreviewUrl) {
      URL.revokeObjectURL(this.expensePreviewUrl);
      this.expensePreviewUrl = null;
    }
    if (file && file.type.startsWith('image/')) {
      this.expensePreviewUrl = URL.createObjectURL(file);
    }
  }

  createExpense(): void {
    if (!this.expenseTitle.trim() || !this.expenseAmount || this.expenseAmount <= 0) {
      this.error.set('Expense title and a positive amount are required.');
      return;
    }
    if (!this.expenseCategory) {
      this.error.set('Select an expense category.');
      return;
    }

    const form = new FormData();
    form.append('Title', this.expenseTitle.trim());
    form.append('Amount', String(this.expenseAmount));
    form.append('ExpenseDate', this.expenseDate);
    form.append('Category', this.expenseCategory);
    if (this.expenseDescription) form.append('Description', this.expenseDescription);
    form.append('Currency', 'USD');
    form.append('SettleAsIncome', String(this.settleAsIncome));
    if (this.expenseFile) {
      form.append('invoiceScreenshot', this.expenseFile, this.expenseFile.name);
    }

    this.http
      .post<ApiResponse<ExpenseRow>>(`${environment.apiUrl}/v1/expenses/with-receipt`, form)
      .pipe(
        map((res) => {
          if (!res?.success) throw new Error(res?.message || 'Failed to save expense.');
          return res.data;
        })
      )
      .subscribe({
        next: () => {
          this.info.set(
            this.settleAsIncome
              ? 'Expense saved and settled with a matching income.'
              : 'Expense saved.'
          );
          this.expenseTitle = '';
          this.expenseAmount = null;
          this.expenseDescription = '';
          this.expenseFile = null;
          this.settleAsIncome = false;
          if (this.expensePreviewUrl) {
            URL.revokeObjectURL(this.expensePreviewUrl);
            this.expensePreviewUrl = null;
          }
          this.reload();
        },
        error: (err) => this.error.set(err?.error?.message || err?.message || 'Failed to save expense.')
      });
  }

  deleteExpense(row: ExpenseRow): void {
    this.api.delete(`v1/expenses/${row.id}`).subscribe({
      next: () => this.reload(),
      error: (err) => this.error.set(err?.message || 'Failed to delete expense.')
    });
  }

  exportCsv(kind: 'income' | 'expense'): void {
    this.error.set(null);
    this.info.set(null);
    const category = kind === 'income' ? this.incomeCsvCategory : this.expenseCsvCategory;
    const path = kind === 'income' ? 'v1/incomes/export' : 'v1/expenses/export';
    const params: Record<string, string> = {};
    if (category) params['category'] = category;

    this.http
      .get(`${environment.apiUrl}/${path}`, {
        params,
        responseType: 'blob'
      })
      .subscribe({
        next: (blob) => {
          const suffix = category ? category.replace(/\s+/g, '_') : 'all';
          this.downloadBlob(blob, `${kind}s_${suffix}.csv`);
          this.info.set(
            category
              ? `Exported ${kind} CSV for category "${category}".`
              : `Exported ${kind} CSV for all categories.`
          );
        },
        error: async (err) => {
          const message = await this.readBlobError(err);
          this.error.set(message || `Failed to export ${kind} CSV.`);
        }
      });
  }

  downloadTemplate(kind: 'income' | 'expense'): void {
    const category =
      (kind === 'income' ? this.incomeCsvCategory : this.expenseCsvCategory) ||
      this.categories()[0]?.name ||
      'Miscellaneous';
    const today = new Date().toISOString().slice(0, 10);
    const csv =
      kind === 'income'
        ? [
            'Title,Amount,Currency,IncomeDate,Category,Source,ReferenceNumber,Notes,Description',
            `Sample payment,1000.00,USD,${today},${this.csvEscape(category)},Client A,INV-001,Optional notes,Optional description`
          ].join('\n')
        : [
            'Title,Amount,Currency,ExpenseDate,Category,Description',
            `Sample bill,250.00,USD,${today},${this.csvEscape(category)},Optional description`
          ].join('\n');

    this.downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8' }), `${kind}_import_template.csv`);
    this.info.set(`Downloaded ${kind} CSV template. Category column can be overridden on import.`);
  }

  onImportCsv(event: Event, kind: 'income' | 'expense'): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;

    this.error.set(null);
    this.info.set(null);

    const form = new FormData();
    form.append('file', file, file.name);
    const category = kind === 'income' ? this.incomeCsvCategory : this.expenseCsvCategory;
    if (category) form.append('category', category);

    const path = kind === 'income' ? 'v1/incomes/import' : 'v1/expenses/import';
    this.http.post<ApiResponse<{ imported: number; failed: number; errors?: string[] }>>(
      `${environment.apiUrl}/${path}`,
      form
    ).subscribe({
      next: (res) => {
        if (!res?.success) {
          this.error.set(res?.message || `Failed to import ${kind} CSV.`);
          return;
        }
        const imported = res.data?.imported ?? 0;
        const failed = res.data?.failed ?? 0;
        const detail = res.data?.errors?.length ? ` First errors: ${res.data.errors.slice(0, 3).join(' | ')}` : '';
        this.info.set(
          (res.message || `Imported ${imported} ${kind} row(s).`) +
            (failed ? ` ${failed} row(s) skipped.` : '') +
            (category ? ` Forced category: "${category}".` : '') +
            detail
        );
        this.reload();
      },
      error: (err) => this.error.set(err?.error?.message || err?.message || `Failed to import ${kind} CSV.`)
    });
  }

  private downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  private csvEscape(value: string): string {
    if (/[",\n]/.test(value)) return `"${value.replace(/"/g, '""')}"`;
    return value;
  }

  private async readBlobError(err: unknown): Promise<string | null> {
    const httpErr = err as { error?: Blob; message?: string };
    if (httpErr?.error instanceof Blob) {
      try {
        const text = await httpErr.error.text();
        const parsed = JSON.parse(text) as { message?: string };
        return parsed.message || text;
      } catch {
        return httpErr.message || null;
      }
    }
    return (err as { message?: string })?.message || null;
  }

  screenshotUrl(row: ExpenseRow): string | null {
    if (!row.receiptUrl) return null;
    if (row.receiptUrl.startsWith('http')) return row.receiptUrl;
    return `${environment.apiOrigin}${row.receiptUrl}`;
  }

  money(amount: number, currency = 'USD'): string {
    return `${currency} ${Number(amount ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
}
